import { action, computed, observable, reaction } from "mobx";
import { cloneDeep, debounce, isEqual, sortBy } from "lodash";
import { FormStore } from "@modules/formx";
import {
  Consultant,
  Customer,
  ConsultantContract_GetConsultant,
  ConsultantBookingContract_FetchDepts,
  ConsultantBookingContract_GetCustomer,
  ConsultantBookingContract_Edit,
  ConsultantBookingContract_Add,
  ConsultantBookingContractCustomerFragment,
  ConsultantBookingContract_getContractsByConsultantId,
  ContractsPriceTemplateFragment,
  HighHolidayOptionFragment,
  ConsultantContract_getCustomerPriceList,
  ContractsPriceTemplates,
  ConsultantContract_getHighHolidays,
  NodeType,
  ConsultantBookingContract_getByIdForEdit,
  ConsultantBookingContract_getByIdForDuplication,
  PublishConsultantBookingContract,
  GetConsultantBookingContracts
} from "@graphql";
import { getObservableQuery } from "@graphql/getObservableQuery";
import { FormData, initAddress } from "./utils";
import { store } from "@store";
import locale from "@bonliva-crm/locale";
import { FieldProps } from "@consultants/ConsultantContract/DynamicFieldsGroup";
import * as f from "./fields";
import { contracts } from "./formSchemas";
import { createObjectID, omit } from "@bonliva-crm/common";
import { oneSpecificPeriodPrices } from "@consultants/ConsultantContract/utils";
import moment from "moment";
import { CreateTicketData } from "@tickets/TicketCreate/CreateTicketData";
import { TodoTags } from "@bonliva-crm/common";
import { TICKET_SLIDER, TicketCreateLocation } from "@navigation";
import { checkArchivedPriceList, sortPriceOptions, PriceOption } from "../utils";
import { openBlank } from "@clientUtils";
import { mergeHighHolidays } from "../../config_editor/ContractsPriceTemplates/utils";
import { ContractPriceTemplateRecordInput } from "../../config_editor/ContractsPriceTemplates/ContractsPriceTemplateFormInput";
import { ValidateConsultantContractOptionResult } from "@bonliva-crm/timereport-api-client-js";
import { timeReportService } from "@api/timereports";
import WeeklyHoursWorkedBonus from "@consultants/components/Bonus/WeeklyHoursWorkedBonus";
import WorkedHourBonus from "@consultants/components/Bonus/WorkedHourBonus";
import WorkedHourPeriodBonus from "@consultants/components/Bonus/WorkedHourBonus";
import OvertimeBonus from "@consultants/components/Bonus/OvertimeBonus";
import CustomBonus from "@consultants/components/Bonus/CustomBonus";
import { Tenant } from "@bonliva-crm/shared";

export { FormData };

type CustomerContracts =
  ConsultantContract_getCustomerPriceList.Return["getCustomerContractPriceLists"];
type CustomerPriceListItem = CustomerContracts[0]["priceLists"][0];

export class ContractFormStore {
  form: FormStore<FormData>;
  handleClose: () => {};

  @observable tenant: Tenant = null;
  @observable isLoadingDepartments = false;
  @observable loading = false;
  @observable isEdit = false;
  @observable action: string = null;
  @observable isTicket = false;
  @observable isPublishingToOneFlow = false;
  @observable ticketData = null;
  @observable defaultTemplateWarning: string = null;
  @observable preFilledData: FormData = null;
  @observable fields: FieldProps[] = [f.contractType];
  @observable allDepartments: LabelValue[] = null;
  @observable contactPersons: LabelValue[] = null;
  @observable contactPersonsFullInfo: any[] = null;
  @observable selectedCustomer:
    | ConsultantBookingContractCustomerFragment
    | null
    | Partial<Customer> = null;
  @observable currentConsultant: Partial<Consultant> | null = null;
  @observable templates: ContractsPriceTemplateFragment[];
  @observable highHolidaysOptions: HighHolidayOptionFragment[];
  @observable fav: ContractsPriceTemplateFragment[];
  @observable
  customerPriceTemplates: ContractsPriceTemplateFragment[];
  @observable customerPriceOptions: PriceOption[];
  @observable
  customerContracts: CustomerContracts;
  selectedCustomerTemplate: ContractsPriceTemplateFragment;
  @observable
  initialData: ConsultantBookingContract_getByIdForEdit.Return["getConsultantBookingContractDetailInfo"];

  @observable validationResult: ValidateConsultantContractOptionResult[] = [];

  bonusTypes: string[] = [
    "WeeklyHoursWorkedBonus",
    "WorkedHourBonus",
    "OvertimeBonus",
    "CustomBonus"
  ];

  constructor(
    initialValues: FormData,
    consultantId: string,
    handleClose,
    tenant: Tenant,
    contractId?: string,
    action?: string
  ) {
    this.form = new FormStore<FormData>({
      onSubmit: this.onSubmit,
      initialValues,
      onSetValue: this._onSetValue
    });

    this.handleClose = handleClose;
    this.isEdit = action === "edit";
    this.action = action;
    this.tenant = tenant;
    this.getCurrentConsultant(consultantId);
    this.getPriceTemplates();
    this.getHolidayOptions();

    if (contractId) {
      this.getContractData(contractId, action);
    }

    // Both observables must be present before the body runs. Two parallel
    // GraphQL queries fire in the constructor:
    //   - ConsultantBookingContract_getByIdForEdit (fast → preFilledData)
    //   - ConsultantContract_GetConsultant (slow → currentConsultant)
    // The reaction body reads `this.currentConsultant.ResponsiblePerson` and
    // `.Name`, so observing only `preFilledData` lets the body run with
    // `currentConsultant` still null when contract data resolves first. Gate
    // on both being set; MobX will re-fire the reaction when either changes.
    reaction(
      () => ({ pre: this.preFilledData, consultant: this.currentConsultant }),
      ({ pre, consultant }) => {
        if (!pre || !consultant) {
          return;
        }
        const { StartDate, EndDate, ...e } = pre;
        if (e) {
          // NOTE: do NOT set `this.isEdit = true` here. `getContractData`
          // already sets isEdit correctly per action: true for "edit", false
          // for "duplicate". MobX reactions fire after the .then microtask,
          // so unconditionally setting true here was clobbering the
          // duplicate path's `isEdit = false` and silently turning duplicate
          // into edit.
          if (StartDate && EndDate) {
            this.form.reset({
              ...e,
              StartDate: moment(StartDate, "x").toDate(),
              EndDate: moment(EndDate, "x").toDate(),
              PriceRecruitment: {
                ...e.PriceRecruitment,
                approve: true
              }
            });
          } else {
            this.form.reset(e);
          }

          if (e.HospitalRef) {
            this.fetchHospitalData(e.HospitalRef.value);
          }

          if (!e.ResponsiblePersonId) {
            this.form.setValue(
              "ResponsiblePersonId",
              consultant.ResponsiblePerson?.PersonId
            );
          }

          const CustomerId = e.DepartmentsRefs || e.HospitalRef?.value;
          this.selectCustomer(CustomerId);
          this.fields = contracts[this.form.values.ContractType] ?? [f.contractType];
          this.ticketData = {
            Type: e.ContractType,
            ConsultantName: consultant.Name,
            Dates: `${moment(StartDate, "x").format("YYYY-mm-dd")} - ${moment(
              EndDate,
              "x"
            ).format("YYYY-mm-dd")}`
          };
        }
      }
    );

    (window as any).contractFormStore = this;
  }

  _onSetValue = ({ name, value }) => {
    // change validation form when set ContractType
    if (name === "ContractType") {
      this.fields = contracts[this.form.values.ContractType] ?? [f.contractType];
    }
    // prefill ContactPerson's fields
    if (name === "ContactPersonRef") {
      const { _id, ...matchContact } =
        this.contactPersonsFullInfo?.find((el) => el._id === value) ?? {};
      this.form.values.ContactPerson = { ...matchContact };
    }
    if (this.allDepartments?.length > 0 && !this.form.values.DepartmentsRefs) {
      this.form.setError("DepartmentsRefs", locale.fieldIsMandatory(locale.Department));
    }
    if (name === "AreaOfExpertise" || name === "StartDate" || name === "EndDate") {
      this.formatCustomerPriceTemplatesOptions(this.customerContracts);
    }

    this.validateContractOptionsDebounced();
  };

  validateContractOptions = () => {
    timeReportService
      .validateConsultantContractOptions({
        consultantContractPriceRecordDto: this.form.values.SalaryRecords.filter(
          (x) => !x.deleted
        ).map((x) => ({
          id: x._id,
          price: 0,
          paidPer: x.paidPer || "",
          from: x.from || "",
          to: x.to || "",
          workType: x.workType || "",
          currency: x.currency || "",
          payPeriod: x.period || "",
          name: "",
          endDay: x.endDay || "",
          startDay: x.startDay || "",
          startDate: new Date("2021-01-01").toISOString(),
          endDate: new Date("2029-01-01").toISOString(),
          specialPeriodId: "",
          specialPeriod: {
            id: "",
            title: "",
            price: 0,
            startDate: new Date("2021-01-01").toISOString(),
            endDate: new Date("2029-01-01").toISOString()
          }
        }))
      })
      .then((res) => {
        this.validationResult = res.data;
      })
      .catch((e) => {
        console.error(e);
        this.validationResult = [];
      });
  };

  validateContractOptionsDebounced = debounce(this.validateContractOptions, 1000);

  getPriceTemplates = (text?: string) => {
    ContractsPriceTemplates.func(
      {
        filter: { text },
        skip: 0,
        limit: 0,
        onlyPrivate: false
      },
      { fetchPolicy: "network-only" }
    ).then((res) => {
      if (res.data.list) {
        this.templates = res.data.list.items.filter((x) => !x.private);
        this.fav = res.data.list.items.filter((x) => x.private);
      }
    });
  };

  getHolidayOptions = () => {
    ConsultantContract_getHighHolidays.func({}, { fetchPolicy: "cache-first" }).then(
      (res) => {
        if (res.data.getHighHolidays) {
          this.highHolidaysOptions = res.data.getHighHolidays;
          if (this.form.values.highHolidays?.length === 0) {
            this.form.setValue(
              "highHolidays",
              mergeHighHolidays(this.highHolidaysOptions, [])
            );
          }
        }
      }
    );
  };

  @computed get hasErrorsInMainTab(): boolean {
    return (
      Object.keys(this.form.errors).filter(
        (x) =>
          x !== "BasicPrice" && x !== "SpecificPeriodPrices" && x !== "PriceRecruitment"
      )?.length > 0
    );
  }

  @computed get hasErrorsInSalaryOption(): boolean {
    return this.validationResult?.some((x) => !x.isValid);
  }

  @computed get canSubmit(): boolean {
    return (
      !this.hasErrorsInMainTab && !this.hasErrorsInSalaryOption && this.form.canSubmit
    );
  }

  @computed get diffBetweenCustomerTemplate(): boolean {
    const mergedHH = mergeHighHolidays(
      this.highHolidaysOptions,
      this.selectedCustomerTemplate?.highHolidays
    );
    const customerTemplate = {
      highHolidays: sortBy(
        mergedHH?.map((x: any) => ({
          isGrateHoliday: x.isGreatHoliday,
          name: x.option.name
        })),
        "name"
      ),
      records: this.selectedCustomerTemplate?.records?.map((x) => ({
        articleNumber: x.articleNumber,
        currency: x.currency,
        deleted: x.deleted,
        endDay: x.endDay,
        startDay: x.startDay,
        from: x.from,
        paidPer: x.paidPer,
        period: x.period,
        to: x.to,
        workType: x.workType
      }))
    };
    const currentTemplate = {
      highHolidays: sortBy(
        this.form.values.highHolidays?.map((x) => ({
          isGrateHoliday: x.isGreatHoliday,
          name: x.option.name
        })),
        "name"
      ),
      records: this.form.values.SalaryRecords?.map((x) => ({
        articleNumber: x.articleNumber,
        currency: x.currency,
        deleted: x.deleted,
        endDay: x.endDay,
        startDay: x.startDay,
        from: x.from,
        paidPer: x.paidPer,
        period: x.period,
        to: x.to,
        workType: x.workType
      }))
    };
    // console.log("check", customerTemplate, currentTemplate);
    return !isEqual(customerTemplate, currentTemplate);
  }

  formatCustomerPriceTemplatesOptions = (contracts: CustomerContracts) => {
    // console.log("c", toJS(contracts));

    const priceLists = contracts
      ?.reduce<CustomerPriceListItem[]>((a, c) => {
        return a.concat(c.priceLists);
      }, [])
      .map((x) => ({
        ...x,
        priceGroups: x.priceGroups.filter((prof) => {
          const aoe = this.form.getValue("AreaOfExpertise");
          const profNames = store().cache.getProfessionsOnAreasofExp([aoe] as string[]);
          // console.log(prof.professionsPrice, profNames);
          return this.form.getValue("AreaOfExpertise")
            ? prof.professionsPrice?.any(
                (p) =>
                  p.profession.areaOfExpertise === aoe ||
                  (!p.profession.areaOfExpertise && profNames.includes(p.profession.name))
              )
            : true;
        })
      }));
    // console.log("priceLists", priceLists);
    if (priceLists) {
      this.customerPriceTemplates = priceLists.map((x) => x.priceGroups).flat();
      const options = priceLists
        .map((x) => {
          const archived = checkArchivedPriceList(x, this.form.values);

          return x.priceGroups.map((p, i) => ({
            label: `${x.name} ${moment(x.startDate).format("DD/MM/YYYY")} - ${moment(
              x.endDate
            ).format("DD/MM/YYYY")}`,
            value: p._id,
            archived
          }));
        })
        .flat();

      this.customerPriceOptions = sortPriceOptions(options);
    }
  };

  @action getCustomerContractPriceLists = (customerIds: string[]) => {
    if (customerIds.filter((x) => x)?.length > 0) {
      ConsultantContract_getCustomerPriceList.func(
        {
          consultantId: this.currentConsultant._id,
          customerIds
        },
        { fetchPolicy: "network-only" }
      ).then(({ data }) => {
        if (data.getCustomerContractPriceLists) {
          this.customerContracts = data.getCustomerContractPriceLists;
          this.formatCustomerPriceTemplatesOptions(data.getCustomerContractPriceLists);
        }
      });
    }
  };

  @action getCurrentConsultant = (id) => {
    ConsultantContract_GetConsultant.func({ id }, { fetchPolicy: "network-only" }).then(
      (res) => {
        const { consultantById } = res?.data ?? {};
        if (consultantById.AreasOfExpertise?.length === 1) {
          this.form.values.AreaOfExpertise = consultantById.AreasOfExpertise;
        }
        if (consultantById.Professions?.length === 1) {
          this.form.values.Profession = consultantById.Professions[0];
        }

        if (consultantById.sithsCardNumber) {
          this.form.values.sithsCardNumber = consultantById.sithsCardNumber;
        }

        this.form.values.ConsultantId = consultantById._id;
        this.currentConsultant = consultantById as any;

        this.form.setValue(
          "ResponsiblePersonId",
          consultantById.ResponsiblePerson?.PersonId
        );
      }
    );
  };

  @action selectCustomer = (id) => {
    ConsultantBookingContract_GetCustomer.func(
      { id },
      { fetchPolicy: "network-only" }
    ).then((res) => {
      const { customerById } = res?.data ?? {};
      this.selectedCustomer = customerById;
      this.contactPersonsFullInfo = customerById.ContactPersons;
      this.form.setValue("Address", customerById.Address);
      this.contactPersons = customerById.ContactPersons.toLabelValue(
        (x) => x.Name,
        (x) => x._id
      );
    });
  };
  @action setFormattedDataToForm = (data) => {
    const { Type, HospitalRef, DepartmentsRefs, SignedOn, ...rest } = data;
    this.preFilledData = {
      ...rest,
      ContractType: Type,
      HospitalRef: HospitalRef
        ? { label: HospitalRef.Name, value: HospitalRef.Id }
        : null,
      DepartmentsRefs: DepartmentsRefs ? DepartmentsRefs.Id : null
    };
  };

  @action setNoTemplateWarning = (text: string) => {
    this.defaultTemplateWarning = text;
  };

  @action getContractData = async (contractId, action) => {
    try {
      if (action === "edit") {
        ConsultantBookingContract_getByIdForEdit.func(
          { contractId },
          { fetchPolicy: "network-only" }
        ).then((res) => {
          this.setFormattedDataToForm(res?.data?.getConsultantBookingContractDetailInfo);
          this.isEdit = true;
          this.initialData = res.data.getConsultantBookingContractDetailInfo;
          console.log({
            initialData: res.data.getConsultantBookingContractDetailInfo
          });
        });
      } else {
        ConsultantBookingContract_getByIdForDuplication.func(
          { contractId },
          { fetchPolicy: "network-only" }
        ).then((res) => {
          let data = res?.data?.getConsultantBookingContractDetailInfo;
          if (data.ContractPriceTemplateId) {
            const template = [
              ...this.templates,
              ...this.fav,
              ...this.customerPriceTemplates
            ].find((x) => x._id === data.ContractPriceTemplateId);
            const t = cloneDeep(template);
            if (t) {
              data = {
                ...data,
                SalaryRecords: t.records,
                highHolidays: t.highHolidays
              };
            } else {
              this.setNoTemplateWarning("Oops, det verkar inte finnas någon avtalsmall");
            }
          }
          this.setFormattedDataToForm(data);
          this.isEdit = false;
          // this.toggleOpenContactForm();
        });
      }
    } catch (e) {
      store().snackbars.error(`${e}`);
    }
  };

  @action addRequirements = (name) => {
    this.form.push(name, "");
  };

  @action removeRequirements = (name: string, index: number) => {
    this.form.removeByIndex(name, index);
  };

  @computed get allProfessions() {
    return this.currentConsultant?.Professions || [];
  }

  @computed get allAreasOfExpertise() {
    return this.currentConsultant?.AreasOfExpertise || [];
  }

  @computed get allOfContactPerson() {
    return this.contactPersons || [];
  }

  @computed get allOfContactPersonFullInfo() {
    return this.contactPersonsFullInfo || [];
  }

  @action fetchHospitalData = (id) => {
    if (!id) {
      return;
    }

    this.isLoadingDepartments = true;

    ConsultantBookingContract_FetchDepts.func(
      { hospitalId: id },
      { fetchPolicy: "network-only" }
    ).then(({ data }) => {
      console.log("set c", data.hospital.OrganizationNumber);
      if (!data.hospital.OrganizationNumber) {
        this.form.setError(
          "OrganizationNumber",
          "Kundprofilen saknar organisationsnummer, vänligen gå till kundprofilen och lägg till detta för att fortsätta."
        );
      } else {
        this.form.validate();
      }
      this.allDepartments = data.departments.items.toLabelValue(
        (x) => x.Name,
        (x) => x._id
      );
      this.contactPersonsFullInfo = data.hospital?.ContactPersons;
      this.contactPersons = data.hospital?.ContactPersons?.toLabelValue(
        (x) => x.Name,
        (x) => x._id
      );
      if (this.form.values.DepartmentsRefs) {
        this.contactPersonsFullInfo =
          data.departments?.items.find((x) => x._id === this.form.values.DepartmentsRefs)
            .ContactPersons ?? [];
      }
      if (this.allDepartments?.length > 0 && !this.form.values.DepartmentsRefs) {
        this.form.setError("DepartmentsRefs", locale.fieldIsMandatory(locale.Department));
      }
    });
  };

  @action setHospital = (hospital: Customer) => {
    this.selectedCustomer = hospital;

    if (!hospital) {
      this.form.values.HospitalRef = null;
      this.form.values.DepartmentsRefs = null;
      this.form.values.Address = initAddress;
      this.allDepartments = null;
      this.contactPersons = null;
      this.contactPersonsFullInfo = null;
      this.form.validate();
      return;
    }

    this.form.values.HospitalRef = {
      label: hospital.Name,
      value: hospital._id
    };
    this.form.values.DepartmentsRefs = null;
    this.form.values.Address = hospital.Address;
    this.fetchHospitalData(hospital._id);

    this.form.validate();
  };

  @action setDepartments = (depts) => {
    this.form.values.DepartmentsRefs = depts;
    this.form.validate();
    this.selectCustomer(depts);
  };

  @action setResponsiblePerson = (id) => {
    this.form.setValue("ResponsiblePersonId", id);
  };

  @action addSalary = () => {
    this.form.push("SalaryRecords", ContractPriceTemplateRecordInput.CreateBlank());
  };

  @action duplicateSalary = (item: ContractPriceTemplateRecordInput) => {
    const temp = { ...item };
    temp._id = createObjectID();
    this.form.push("SalaryRecords", temp);
  };

  @action addSpecificPeriodPrice = () => {
    this.form.push("SpecificPeriodPrices", oneSpecificPeriodPrices);
  };

  @action remSpecificPeriodPrice = (index: number) => {
    this.form.removeByIndex("SpecificPeriodPrices", index);
  };

  setDefaultHolidaysAndSalaryRecords = () => {
    const mergedHolidays = mergeHighHolidays(this.highHolidaysOptions, []);
    this.form.setValue(`SalaryRecords`, [ContractPriceTemplateRecordInput.CreateBlank()]);
    this.form.setValue(`highHolidays`, mergedHolidays);
  };

  @action handleChangeTemplate = (v) => {
    if (!v) {
      this.form.setValue(`template`, v);
      this.setDefaultHolidaysAndSalaryRecords();
      return;
    }
    const template = this.templates.find((x) => x._id === v);
    const mergedHolidays = mergeHighHolidays(
      this.highHolidaysOptions,
      template.highHolidays
    );
    this.form.setValue(`template`, v);
    this.form.setValue(`favTemplate`, null);
    this.form.setValue(`customerTemplate`, null);
    this.form.setValue(`SalaryRecords`, template?.records);
    this.form.setValue(`highHolidays`, mergedHolidays);
  };

  @action handleChangeFavTemplate = (v) => {
    if (!v) {
      this.form.setValue(`favTemplate`, v);
      this.setDefaultHolidaysAndSalaryRecords();
      return;
    }
    const template = this.fav.find((x) => x._id === v);

    const mergedHolidays = mergeHighHolidays(
      this.highHolidaysOptions,
      template.highHolidays
    );

    this.form.setValue(`favTemplate`, v);
    this.form.setValue(`template`, null);
    this.form.setValue(`customerTemplate`, null);
    this.form.setValue(`SalaryRecords`, template?.records);
    this.form.setValue(`highHolidays`, mergedHolidays);
  };

  @action handleChangeCustomerTemplate = (v) => {
    if (!v) {
      this.form.setValue(`customerTemplate`, v);
      this.setDefaultHolidaysAndSalaryRecords();
      return;
    }
    const template = this.customerPriceTemplates.find((x) => x._id === v);
    this.selectedCustomerTemplate = template;
    const mergedHolidays = mergeHighHolidays(
      this.highHolidaysOptions,
      template?.highHolidays
    );

    const { records } = template;

    this.form.setValue(`customerTemplate`, v);
    this.form.setValue(`template`, null);
    this.form.setValue(`favTemplate`, null);
    this.form.setValue(`SalaryRecords`, records);
    this.form.setValue(`highHolidays`, mergedHolidays);
  };

  @action pushToArr = (name, value) => {
    if (this.form.getValue(name)) {
      this.form.push(name, value);
    } else {
      this.form.setValue(name, [value]);
    }
  };

  @action removeByIndex = (name: string, index: number) => {
    this.form.removeByIndex(name, index);
  };

  @action handleSaveAndSendToOneflow = () => {
    this.isPublishingToOneFlow = true;
    this.form
      .submitAsync()
      .then((result) => {
        return PublishConsultantBookingContract.func({
          contractId: result._id
        });
      })
      .then(
        ({ data }) => {
          const url = data.publishBookingContract.contractUrl;
          openBlank(url);
          store().snackbars.success("Published");
          this.handleClose();
          this.isPublishingToOneFlow = false;

          getObservableQuery(
            ConsultantBookingContract_getContractsByConsultantId.OperationName
          )?.refetch();
        },
        (error) => {
          this.isPublishingToOneFlow = false;
        }
      );
  };

  @action private onSubmit = async () => {
    return this.submit()
      .then((result) => {
        this.handleClose();
        if (this.isTicket) {
          this.handleTicket({
            ...result,
            ConsultantName: this.currentConsultant.Name
          });
        }
        return result; // results is used in chain when you publish contract to oneflow
      })
      .catch((e) => {
        store().snackbars.error(`${e}`);
      })
      .finally(() => {
        this.loading = false;
        this.isTicket = false;
      });
  };

  @action private submit = async () => {
    const { valid, values } = this.form.state;
    if (valid) {
      this.loading = true;
      const {
        _id,
        ConsultantId,
        ContactPersonRef,
        template,
        favTemplate,
        customerTemplate,
        PriceRecruitment,
        bonusType,
        ...rest
      } = values;
      const HospitalRef = values.HospitalRef ? { Id: values.HospitalRef?.value } : null;
      const DepartmentsRefs = values.DepartmentsRefs
        ? { Id: values.DepartmentsRefs }
        : null;

      const BasicPrice = Number(values.BasicPrice);
      const Bonus = values.Bonus?.map((x) => {
        delete x["SalaryType"];
        return {
          ...x,
          Amount: Number(x.Amount)
        };
      });
      const IntroductionPrice = values.IntroductionPrice?.filter(
        (el) => el.Description.length > 0 && Number(el.Sum) > 0
      ).map((x) => ({ ...x, Sum: Number(x.Sum) }));
      const SalaryRecords = values.SalaryRecords.filter((x) => !x.deleted).map(
        (x) =>
          omit(
            {
              ...x,
              articleNumber: x.articleNumber || null,
              paidPer: x.paidPer || null,
              currency: x.currency || null,
              workType: x.workType || null,
              period: x.period || null,
              from: x.from || null,
              to: x.to || null,
              endDay: x.endDay || null,
              startDay: x.startDay || null,
              fixedPrice: x.fixedPrice ? Number(x.fixedPrice) : null,
              priceMultiplier: x.priceMultiplier ? Number(x.priceMultiplier) : null,
              customPrice: x.customPrice ? Number(x.customPrice) : null
            },
            "id",
            "isFreeInput"
          ) as any
      );

      const SpecificPeriodPrices = values.SpecificPeriodPrices?.filter(
        (el) => Number(el.price) > 0
      ).map((x) => ({ ...x, price: Number(x.price) }));
      const SpecificRequirements = values.SpecificRequirements.filter((el) => el?.length);
      let restPriceRecruitment = null;

      if (PriceRecruitment) {
        restPriceRecruitment = {
          Currency: PriceRecruitment.Currency,
          R_0_159: Number(PriceRecruitment.R_0_159),
          R_160_499: Number(PriceRecruitment.R_160_499),
          R_500_699: Number(PriceRecruitment.R_500_699),
          R_700_899: Number(PriceRecruitment.R_700_899),
          R_900_1200: Number(PriceRecruitment.R_900_1200)
        };
      }

      const data = {
        ...rest,
        PriceRecruitment: restPriceRecruitment,
        HospitalRef,
        DepartmentsRefs,
        BasicPrice,
        Bonus,
        IntroductionPrice,
        SpecificPeriodPrices,
        SalaryRecords,
        SpecificRequirements
      };

      const promise = this.isEdit
        ? ConsultantBookingContract_Edit.func(
            { contractId: _id, data },
            {
              refetchQueries: [
                ConsultantBookingContract_getContractsByConsultantId.OperationName,
                GetConsultantBookingContracts.OperationName
              ]
            }
          ).then((result) => {
            store().snackbars.success(locale.EditContractSuccess);
            return result.data.editConsultantBookingContract;
          })
        : ConsultantBookingContract_Add.func(
            {
              consultantId: ConsultantId,
              data
            },
            {
              refetchQueries: [
                ConsultantBookingContract_getContractsByConsultantId.OperationName,
                GetConsultantBookingContracts.OperationName
              ]
            }
          ).then((result) => {
            store().snackbars.success(locale.AddContractSuccess);
            return result.data.addConsultantBookingContract;
          });

      return promise;
    }
  };

  @action handleTicket = (data) => {
    const ticket = CreateTicketData.Empty();

    ticket.title = `Skicka ${locale[data.Type]}${
      data.ConsultantName && ` till kund gällande ${data.ConsultantName}`
    }, ${data.Dates}`;

    ticket.tags.push(TodoTags.CONTRACT);
    ticket.linkedItems.push({
      NodeType: NodeType.Consultant,
      NodeID: this.currentConsultant._id,
      Name: this.currentConsultant.Name || "saknas"
    });
    ticket.assignedTeam = "FinanceAgreement";
    ticket.assignedUser = null;
    ticket.dueDateText =
      "Tickets som skickas innan 16.00 hanteras samma dag. Tickets som skickas efter 16.00 hanteras nästkommande förmiddag.";
    ticket.dueDateDisabled = true;

    const { dueDateDisabled, dueDateText } = ticket;

    store().navigation.open(
      TICKET_SLIDER,
      new TicketCreateLocation({
        initialValues: ticket,
        onCreated: () => {
          PublishConsultantBookingContract.func({
            contractId: data._id
          });
        },
        dueDateProps: {
          disabled: dueDateDisabled,
          DatePickerProps: {
            helperText: dueDateText
          }
        }
      })
    );
  };

  @action handleChangeBonusType = (value) => {
    this.form.setValue("bonusType", value);
  };

  @action addBonusRecord = () => {
    let bonusBlank = null;
    if (this.form.values.bonusType === "WeeklyHoursWorkedBonus") {
      bonusBlank = WeeklyHoursWorkedBonus.BlankState();
    } else if (this.form.values.bonusType === "WorkedHourBonus") {
      bonusBlank = WorkedHourBonus.BlankState();
    } else if (this.form.values.bonusType === "WorkedHourPeriodBonus") {
      bonusBlank = WorkedHourPeriodBonus.BlankState();
    } else if (this.form.values.bonusType === "OvertimeBonus") {
      bonusBlank = OvertimeBonus.BlankState();
    } else if (this.form.values.bonusType === "CustomBonus") {
      bonusBlank = CustomBonus.BlankState();
    }

    if (bonusBlank) {
      this.form.push("Bonus", bonusBlank);
      this.form.setValue("bonusType", null);
    }
  };

  @action removeBonusRecord = (index: number) => {
    this.form.removeByIndex("Bonus", index);
  };
}
